import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Stack,
  FormControlLabel,
  Switch,
  AppBar,
  Toolbar,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PreviewIcon from '@mui/icons-material/Preview';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { AuthContext } from '../../App';
import api from '../../api';

function AdminNewsEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { admin } = useContext(AuthContext);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    imageUrl: '',
    videoUrl: '',
    published: false,
    publishedAt: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    if (!isNew) {
      fetchPost();
    }
  }, [id]);

  const fetchPost = async () => {
    try {
      const post = await api.adminGetNewsPost(admin.token, id);
      setForm({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        imageUrl: post.imageUrl || '',
        videoUrl: post.videoUrl || '',
        published: post.published || false,
        publishedAt: post.publishedAt
          ? new Date(post.publishedAt).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0],
      });
    } catch (err) {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setForm({
      ...form,
      title,
      slug: isNew || !form.slug ? generateSlug(title) : form.slug,
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const result = await api.uploadImage(admin.token, file);
      setForm({ ...form, imageUrl: result.url });
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, imageUrl: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setSaving(true);

    try {
      const data = {
        ...form,
        publishedAt: new Date(form.publishedAt).toISOString(),
      };

      if (isNew) {
        const newPost = await api.createNewsPost(admin.token, data);
        navigate(`/admin/news/${newPost.id}`);
        setSuccess(true);
      } else {
        await api.updateNewsPost(admin.token, id, data);
        setSuccess(true);
      }
    } catch (err) {
      setError(err.message || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 20 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Button
            component={Link}
            to="/admin"
            color="inherit"
            startIcon={<ArrowBackIcon />}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            {isNew ? 'New Post' : 'Edit Post'}
          </Typography>
          {!isNew && form.published && (
            <Button
              color="inherit"
              component="a"
              href={`/news/${form.slug}`}
              target="_blank"
              startIcon={<PreviewIcon />}
              sx={{ mr: 2 }}
            >
              View
            </Button>
          )}
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
            sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
            Post saved successfully!
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                Post Details
              </Typography>
              <Stack spacing={3}>
                <TextField
                  label="Title"
                  value={form.title}
                  onChange={handleTitleChange}
                  fullWidth
                  required
                  placeholder="Enter post title..."
                />
                <TextField
                  label="URL Slug"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: generateSlug(e.target.value) })}
                  fullWidth
                  required
                  helperText={`Post will be available at /news/${form.slug || 'your-slug'}`}
                />
                <TextField
                  label="Excerpt"
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Brief summary shown in news listings..."
                />

                {/* Image Upload Section */}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Featured Image
                  </Typography>
                  
                  {form.imageUrl ? (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        component="img"
                        src={form.imageUrl}
                        alt="Featured"
                        sx={{
                          width: '100%',
                          maxHeight: 300,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveImage}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'error.main',
                          color: '#fff',
                          '&:hover': { bgcolor: 'error.dark' },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'primary.50',
                        },
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {uploading ? (
                        <CircularProgress size={40} />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography variant="body1" color="text.secondary">
                            Click to upload an image
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            JPG, PNG, GIF, or WebP (max 10MB)
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  
                  {/* Option to use URL instead */}
                  <TextField
                    label="Or enter image URL"
                    value={form.imageUrl}
                    onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                    fullWidth
                    size="small"
                    placeholder="https://example.com/image.jpg"
                    sx={{ mt: 2 }}
                  />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                YouTube Video (Optional)
              </Typography>
              <TextField
                label="YouTube URL"
                value={form.videoUrl}
                onChange={(e) => setForm({ ...form, videoUrl: e.target.value })}
                fullWidth
                size="small"
                placeholder="https://www.youtube.com/watch?v=..."
                helperText="Paste a YouTube video URL to embed in the post"
              />
              {form.videoUrl && form.videoUrl.includes('youtube') && (
                <Box sx={{ mt: 2, borderRadius: 1, overflow: 'hidden' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Preview:
                  </Typography>
                  <Box
                    component="iframe"
                    src={`https://www.youtube.com/embed/${form.videoUrl.match(/(?:v=|youtu\.be\/)([^&\s]+)/)?.[1] || ''}`}
                    sx={{ width: '100%', height: 200, border: 'none', borderRadius: 1 }}
                    allowFullScreen
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                Content
              </Typography>
              <TextField
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                fullWidth
                multiline
                rows={15}
                placeholder="Write your post content here... You can use HTML for formatting."
                helperText="Supports HTML: <p>, <h2>, <h3>, <strong>, <em>, <a>, <ul>, <li>, <img>"
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                Publishing
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center">
                <TextField
                  label="Publish Date"
                  type="date"
                  value={form.publishedAt}
                  onChange={(e) => setForm({ ...form, publishedAt: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.published}
                      onChange={(e) => setForm({ ...form, published: e.target.checked })}
                      color="success"
                    />
                  }
                  label={form.published ? 'Published' : 'Draft'}
                />
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  sx={{ px: 4 }}
                >
                  {saving ? 'Saving...' : isNew ? 'Create Post' : 'Update Post'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </form>

        {/* Preview Section */}
        {form.content && (
          <Card sx={{ mt: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                Preview
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Box
                sx={{
                  '& p': { mb: 2 },
                  '& h2, & h3': { mt: 3, mb: 2 },
                  '& a': { color: 'primary.main' },
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                  '& ul, & ol': { pl: 3, mb: 2 },
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(form.content) }}
              />
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
}

export default AdminNewsEditor;
